import './DRManagement.css';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
// Components
import DRTableItem from './component/DRTableItem';
import DRAppendSideModal from './component/DRAppendSideModal';
import DRUpdateSideModal from './component/DRUpdateSideModal';
import DRDeleteModal from './component/DRDeleteModal';
import DRDetailModal from './component/DRDetailModal';
import ChargerCompModal from './component/ChargerCompModal';
import Pagination from '../../components/Pagination/Pagination';
import { SkeletonTable } from '../../components/Skeleton';
// Icons
import ExcelIcon from '../../assets/icons/excel.svg?react';
import PlusIcon from '../../assets/icons/plus.svg?react';
// Hooks
import SearchBox from '../../components/SearchBox/SearchBox';

import useDRResourcesSimple from '../../hooks/useDRResourcesSimple';
// React Query
import { useQueryClient } from '@tanstack/react-query';
// React Router
import { useSearchParams } from 'react-router-dom';


const DRManagement = () => {
  // Refs
  const selectedFilterRef = useRef('');
  const searchValueRef = useRef('');
  // States
  const [canSearch, setCanSearch] = useState(false);
  const [keywordInput, setKeywordInput] = useState(''); // 입력 중인 키워드 (로컬 state)
  const [drInfoForChargerComp, setDrInfoForChargerComp] = useState(null);
  const [chargerCompModalOn, setChargerCompModalOn] = useState(false);
  const [deleteModalOn, setDeleteModalOn] = useState(false);
  const [editModalOn, setEditModalOn] = useState(false);
  const [appendModalOn, setAppendModalOn] = useState(false);
  const [drDetailModal, setDrDetailModal] = useState(null);
  const [target, setTarget] = useState(null);
  
  // URL 검색 파라미터 관리
  const [searchParams, setSearchParams] = useSearchParams();
    
  // Pagination State
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const pageSize = parseInt(searchParams.get('pageSize')) || 10;
  const searchQuery = searchParams.get('search') || '';
  const keyword = searchParams.get('Keyword') || '';
  const sortBy = searchParams.get('sortBy') || 'CreatedDate';
  const sortOrder = searchParams.get('sortOrder') || 'desc';
  
  // keywordInput을 URL의 keyword와 동기화 (초기값 설정)
  useEffect(() => {
    if (keyword) {
      setKeywordInput(keyword);
    } else {
      setKeywordInput('');
    }
  }, [keyword]);
  
  // Filter Parameters (URL from SearchParams)
  const resourceName = searchParams.get('ResourceName') || '';
  const regionSido = searchParams.get('RegionSido') || '';
  const regionSiGunGu = searchParams.get('RegionSiGunGu') || '';
  const regionEupMyeonDong = searchParams.get('RegionEupMyeonDong') || '';

  // React Query Client
  const queryClient = useQueryClient();

  // Query Parameters Memoization (URL State and Link)
  const queryParams = useMemo(() => {
    const params = {
      Page: currentPage,
      PageSize: pageSize,
      OrderBy: sortBy,
      OrderDirection: sortOrder,
    };
    
    // Keyword 파라미터 추가
    if (keyword) {
      params.Keyword = keyword;
    }
    
    // 검색 파라미터 추가 (하나만 활성화)
    if (resourceName) {
      params.ResourceName = resourceName;
    } else if (regionSido) {
      params.RegionSido = regionSido;
    } else if (regionSiGunGu) {
      params.RegionSiGunGu = regionSiGunGu;
    } else if (regionEupMyeonDong) {
      params.RegionEupMyuonDong = regionEupMyeonDong;
    } else if (searchQuery) {
      params.search = searchQuery;
    }
    
    return params;
  }, [currentPage, pageSize, sortBy, sortOrder, keyword, searchQuery, resourceName, regionSido, regionSiGunGu, regionEupMyeonDong]);

  // React Query
  const { 
    drResources, 
    isLoading: drResourcesLoading, 
    error: drResourcesError,
    totalCount,
  } = useDRResourcesSimple(queryParams);

  useEffect(() => {
    if (drResourcesError) {
      console.error('DR 자원 데이터 가져오기 실패:', drResourcesError);
    }
  }, [drResourcesError]);


  const onCloseDeleteModal = () => {
    setDeleteModalOn(false);
    setTarget(null);
  }

  const onDeleteSuccess = () => {
    // 충전기 관련 쿼리 캐시 무효화하여 데이터 다시 불러오기
    queryClient.invalidateQueries({ queryKey: ['drResources'] });
    setTarget(null);
  }

  const onAppendSuccess = () => {
    // 충전기 관련 쿼리 캐시 무효화하여 데이터 다시 불러오기
    queryClient.invalidateQueries({ queryKey: ['drResources'] });
  }

  const onEditSuccess = () => {
    // DR자원 관련 쿼리 캐시 무효화하여 데이터 다시 불러오기
    queryClient.invalidateQueries({ queryKey: ['drResources'] });
  }


  const onDrDetail = (demandResource) => {
    setDrDetailModal(demandResource);
  }

  const onChargerComp = () => {
    setDrDetailModal(null);
    setDrInfoForChargerComp(drDetailModal);
    setChargerCompModalOn(true);
  }
  const onChargerCompClose = () => {
    setChargerCompModalOn(false);
    setDrInfoForChargerComp(null);
    setDrDetailModal(drInfoForChargerComp);
  }


  // URL 상태 업데이트 함수들
  const updateSearchParams = (newParams) => {
    const current = Object.fromEntries(searchParams.entries());
    setSearchParams({ ...current, ...newParams });
  };

  const handlePageChange = (page) => {
    updateSearchParams({ page: page.toString() });
  };

  const handleSearch = (query, filterType) => {
    const newParams = { page: '1' }; // 검색 시 첫 페이지로 이동
    
    if (filterType && filterType !== '') {
      // 특정 필터가 선택된 경우
      newParams[filterType] = query;
    } else {
      // 필터가 선택되지 않은 경우 기본 search 파라미터 사용
      newParams.search = query;
    }
    
    // 기존의 모든 검색 관련 파라미터들을 제거하고 새로운 파라미터만 설정
    const current = Object.fromEntries(searchParams.entries());
    const filteredParams = Object.fromEntries(
      Object.entries(current).filter(([key]) => 
        !['search', 'ResourceName', 'RegionSido', 'RegionSiGunGu', 'RegionEupMyeonDong'].includes(key)
      )
    );
    
    setSearchParams({ ...filteredParams, ...newParams });
  };

  const handleSort = (field, order) => {
    updateSearchParams({ sortBy: field, sortOrder: order, page: '1' }); // 정렬 시 첫 페이지로 이동
  };

  // Keyword 입력 핸들러 (로컬 state만 업데이트)
  const handleKeywordChange = (e) => {
    setKeywordInput(e.target.value);
  };

  // Keyword 입력에서 Enter 키 처리 (쿼리 실행)
  const handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = e.target.value.trim();
      const current = Object.fromEntries(searchParams.entries());
      
      if (value && value.length >= 2) {
        setSearchParams({ ...current, Keyword: value, page: '1' });
      } else {
        // 빈 값이면 Keyword 파라미터 제거
        const { Keyword, ...rest } = current;
        setSearchParams({ ...rest, page: '1' });
      }
    }
  };
  
  const onDelete = (drResource) => {
    const targetObj = {
      drResource: drResource,
  
    }
    setTarget(targetObj);
    setDeleteModalOn(true);
  }
  const onEdit = (drResource) => {
    const targetObj = {
      drResource: drResource,
  
    }
    setTarget(targetObj);
    setEditModalOn(true);
  }

  return (
    <div className="station-management-container page-container">      
      <div className="station-management-content">

        <div className="flex justify-between align-center mb-10">
          <div className="w-half flex">
            <input 
              type="text" 
              placeholder="키워드 검색 (2자 이상) - DR자원 이름, 시도, 시군구, 읍면동" 
              className="input-primary flex-1 text-b2-400" 
              value={keywordInput}
              onChange={handleKeywordChange}
              onKeyDown={handleKeywordKeyDown}
            />
          </div>
          <div className="flex justify-end gap-5">
            <>
              {/* <SearchBox 
                  placeholder="검색" 
                  filters={[
                    {id: 'ResourceName', label: 'DR자원 이름'}, 
                    {id: 'RegionSido', label: '시도'}, 
                    {id: 'RegionSiGunGu', label: '시군구'}, 
                    {id: 'RegionEupMyeonDong', label: '읍면동'}
                  ]} 
                  onFilterChange={() => {}} 
                  onSearch={handleSearch} 
                  defaultValue={resourceName || regionSido || regionSiGunGu || regionEupMyeonDong || searchQuery}
                  selectedFilterRef={selectedFilterRef}
                  searchValueRef={searchValueRef}
                  initialSelectedFilter={resourceName ? 'ResourceName' : regionSido ? 'RegionSido' : regionSiGunGu ? 'RegionSiGunGu' : regionEupMyeonDong ? 'RegionEupMyeonDong' : ''}
                  onSearchStateChange={setCanSearch}
                /> */}
                {/* <button 
                  className={`btn plain btn-style-2 px-10 py-5 ${!canSearch ? 'opacity-50 cursor-not-allowed' : ''}`} 
                  onClick={() => canSearch && handleSearch(searchValueRef.current, selectedFilterRef.current)}
                  disabled={!canSearch}
                >
                  검색
                </button> */}
                {/* <button className="btn big-btn btn-green-700 flex align-center gap-3" onClick={() => {}}><ExcelIcon /> 스프레드 시트 다운로드</button> */}
                <button className="btn big-btn btn-primary flex align-center gap-3" onClick={() => setAppendModalOn(true)}><PlusIcon /> DR자원 추가</button>
              </>
            </div>
        </div>

        <div className="primary-table">
          {drResourcesLoading && (
            <SkeletonTable rows={8} columns={8} />
          )}
          
          {drResourcesError && (
            <div className="error-container">
              <p>데이터를 불러오는데 실패했습니다</p>
            </div>
          )}
          {!drResourcesLoading && !drResourcesError && (
          <table>
            <thead>
              <tr>
                <th>번호</th>
                <th>DR자원 이름</th>
                <th>참여 DR 제도</th>
                <th>시도</th>
                <th>할당 충전소 수</th>
                <th>할당 충전기 수</th>
                <th>OPM 등록 계약번호 수</th>
                <th></th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {drResources.length === 0 ? (
                <tr>
                  <td colSpan="10" className="no-data">
                    DR 자원 데이터가 없습니다.
                  </td>
                </tr>
              ) : (
              drResources.map((resource,idx) => {
                const uniqueId = resource.DrResourceSysId || `temp-${currentPage}-${idx}`;
                const displayIndex = (currentPage - 1) * pageSize + idx + 1;
                return (
                  <DRTableItem key={`${uniqueId}-${currentPage}-${idx}`} resource={resource} idx={displayIndex} onDrDetail={onDrDetail} onDelete={onDelete} onEdit={onEdit} />
                );
              })
            )}
            </tbody>
          </table>
          )}
        </div>
        {/* 페이지네이션 */}
        {!drResourcesLoading && !drResourcesError && (
          <div className="pagination-wrapper">
          <Pagination
            currentPage={currentPage}
            totalPages={Math.max(1, Math.ceil(totalCount / pageSize))}
            onPageChange={handlePageChange}
          />
          </div>
        )}
      </div>

      {chargerCompModalOn && <ChargerCompModal onClose={onChargerCompClose} demandResource={drInfoForChargerComp} />}
      {appendModalOn && <DRAppendSideModal onClose={() => setAppendModalOn(false)} onSuccess={onAppendSuccess} />}
      {editModalOn && <DRUpdateSideModal onClose={() => setEditModalOn(false)} target={target.drResource} onSuccess={onEditSuccess} />}
      {deleteModalOn && <DRDeleteModal onClose={() => setDeleteModalOn(false)} drResource={target.drResource} onDeleteSuccess={onDeleteSuccess} />}
      {drDetailModal && <DRDetailModal onClose={() => setDrDetailModal(null)} demandResource={drDetailModal} onChargerComp={onChargerComp} />}
    </div>
  );
};

export default DRManagement; 